import { useSyncExternalStore } from "react";

type AgentStoreState = {
  isOpen: boolean;
  /**
   * The "start a new conversation?" confirmation card. Lives here rather than in
   * `Agent.tsx` because it is opened from two very different places: the header
   * icon, and the button message the controller renders when a conversation has
   * expired — and the latter has no access to component state.
   */
  isNewSessionDialogOpen: boolean;
  /**
   * Mobile bottom-sheet state (MWR-609): set when the agent navigates the page,
   * so the change is visible behind the chat instead of hidden under the
   * full-viewport panel. Inert on `lg+`, where the panel is a side panel that
   * never covers the page.
   */
  isCollapsed: boolean;
};

type Listener = () => void;

let state: AgentStoreState = {
  isOpen: false,
  isNewSessionDialogOpen: false,
  isCollapsed: false,
};
const listeners = new Set<Listener>();

const emitChange = () => listeners.forEach((l) => l());

const setState = (partial: Partial<AgentStoreState>) => {
  state = { ...state, ...partial };
  emitChange();
};

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

/** Registered by useAgentController on mount. */
let sendUserMessageImpl: ((content: string) => void) | null = null;

export const registerSendUserMessage = (
  fn: (content: string) => void
): void => {
  sendUserMessageImpl = fn;
};

/** Registered by useAgentController on mount. */
let reportPageNotFoundImpl: ((path: string) => void) | null = null;

export const registerReportPageNotFound = (
  fn: (path: string) => void
): void => {
  reportPageNotFoundImpl = fn;
};

/**
 * Programmatic agent controls – can be called from anywhere, no hook needed.
 */
export const agentStore = {
  // Opening always restores the full panel: a fresh question deserves the whole
  // chat, and reopening from the FAB must not land in a stale bottom sheet.
  open: () => setState({ isOpen: true, isCollapsed: false }),
  // Closing the panel also drops the confirmation card: reopening straight into
  // a dialog about a conversation the visitor has since left behind is nonsense.
  close: () =>
    setState({
      isOpen: false,
      isNewSessionDialogOpen: false,
      isCollapsed: false,
    }),
  /** Mobile bottom sheet: shrink the panel so the page behind is visible. */
  collapse: () => setState({ isCollapsed: true }),
  expand: () => setState({ isCollapsed: false }),
  openNewSessionDialog: () => setState({ isNewSessionDialogOpen: true }),
  closeNewSessionDialog: () => setState({ isNewSessionDialogOpen: false }),
  getState: () => state,
  /**
   * Send a message to the agent programmatically.
   * The agent will open and focus automatically.
   * Requires the Agent component to be mounted in the tree.
   */
  sendUserMessage: (content: string) => {
    if (!sendUserMessageImpl) {
      console.warn("agentStore.sendUserMessage: Agent is not mounted yet.");
      return;
    }
    sendUserMessageImpl(content);
  },
  /**
   * Tells the agent that the page it sent the visitor to does not exist, so it
   * can correct course instead of assuming the navigation worked.
   *
   * Invisible in the chat, but it prompts the agent to respond immediately so the
   * visitor is not left on a dead page in silence. A no-op unless a session is
   * live — with no conversation running there is nobody to inform.
   */
  reportPageNotFound: (path: string) => {
    reportPageNotFoundImpl?.(path);
  },
};

/**
 * Reactive hook to read agent widget state in React components.
 * No provider required – works from any Client Component.
 */
export const useAgentStore = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
